import React, { Suspense, lazy } from 'react';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import withFeatureFlags, {
  WithFeatureFlagsProps,
} from '@wix/da-react-context/pkg/flags/withFeatureFlags';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PremiumFolderDataIcon from '@wix/da-shared-react/pkg/GallectionFolderDropdown/_partials/PremiumFolderDataIcon';
import { isDeviationAuthor } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import WidgetBox, { Props as WidgetBoxProps } from '../_partials/WidgetBox';
import FavouritesLabel from '../_partials/FavouritesLabel';
import {
  default as FeaturedDeviation,
  Props as WidgetProps,
} from './FeaturedDeviation';
import EmptyState from '../../EmptyState';
import ChunkLoadingPopover from '../_partials/ChunkLoadingPopover';
import reduxConnectContainer from './FeaturedDeviation.container';

import s from './FeaturedDeviation.scss';

const DeviationPicker = lazy(
  () =>
    import(
      /* webpackChunkName: "owner" */ './EditForm/DeviationPicker/DeviationPickerWithState'
    )
);

const EditForm = lazy(
  () => import(/* webpackChunkName: "owner" */ './EditForm')
);

export interface State {
  showDeviationPicker: boolean;
}
class FeaturedDeviationBox extends React.Component<
  WidgetProps & WidgetBoxProps & WithTranslation & WithFeatureFlagsProps,
  State
> {
  state: State = {
    showDeviationPicker: false,
  };
  render() {
    const {
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      section,
      zone,
      flags,
      profileOwnerUser,
      ...props
    } = this.props;

    const isDeviationUnavailable =
      !props.deviation ||
      (props.deviation.isBlocked && !props.deviation.isMature) || // Mature should show the Dumbo (which supports hidden mature mode)
      props.deviation.isDeleted;

    const emptyStateView = 'large';

    return (
      <>
        {this.state.showDeviationPicker &&
          this.renderDeviationPicker(this.hideDeviationPicker)}
        <WidgetBox
          section={section}
          zone={zone}
          moduleId={props.moduleId}
          header={
            <>
              <PremiumFolderDataIcon
                premiumFolderData={props?.deviation?.premiumFolderData}
                size={IconSize.COMPACT}
                className={s['premium-folder-icon']}
              />
              {props.title || props.t('widgets.featured_deviation.header')}
            </>
          }
          headerExtras={this.renderHeaderExtras()}
          autoShowWidgetConfig={true}
          widgetMenuRenderer={this.renderConfigPopup}
          widgetMoveUp={widgetMoveUp}
          widgetMoveDown={widgetMoveDown}
          widgetRemove={widgetRemove}
          isEmpty={!props.deviation}
          emptyView={
            props.isOwner && (
              <EmptyState
                view={emptyStateView}
                title={props.t('widgets.featured_deviation.empty.title.owner')}
                subtitle={props.t(
                  'widgets.featured_deviation.empty.subtitle.owner'
                )}
                withButton
                buttonLabel={props.t(
                  'widgets.featured_deviation.empty.CTALabel.owner'
                )}
                onButtonClick={this.showDeviationPicker}
              />
            )
          }
        >
          {isDeviationUnavailable && props.isOwner && (
            <EmptyState
              view={emptyStateView}
              title={props.t(
                'widgets.featured_deviation.unavailable.title.owner'
              )}
              subtitle={props.t(
                'widgets.featured_deviation.unavailable.subtitle.owner'
              )}
              withButton
              buttonLabel={props.t(
                'widgets.featured_deviation.empty.CTALabel.owner'
              )}
              onButtonClick={this.showDeviationPicker}
            />
          )}
          {isDeviationUnavailable && !props.isOwner && (
            <EmptyState
              view={emptyStateView}
              title={props.t(
                'widgets.featured_deviation.unavailable.title.visitor'
              )}
              subtitle={props.t(
                'widgets.featured_deviation.unavailable.subtitle.visitor'
              )}
            />
          )}
          {!isDeviationUnavailable && <FeaturedDeviation {...props} />}
        </WidgetBox>
      </>
    );
  }

  renderHeaderExtras = () => {
    const { deviation, profileOwnerUser } = this.props;
    if (
      deviation &&
      !isDeviationAuthor(deviation, profileOwnerUser) &&
      !isGroup(profileOwnerUser)
    ) {
      return <FavouritesLabel tooltipUser={profileOwnerUser} />;
    }
  };

  /** which media types are selectable for display in the widget */
  getMediaSourceTypes = () => {
    // if it's a eclipse group profile page, don't show collections
    const { profileOwnerUser } = this.props;
    return isGroup(profileOwnerUser)
      ? [MediaSourceType.GALLERY]
      : // otherwise, use the DeviationPicker defaults
        undefined;
  };

  renderConfigPopup = close => {
    const { moduleId, isNewlyAddedModule, profileOwnerUser } = this.props;
    if (isNewlyAddedModule) {
      return this.renderDeviationPicker(close);
    }

    return (
      <Suspense fallback={<ChunkLoadingPopover />}>
        <EditForm
          {...this.props}
          moduleId={moduleId}
          mediaSourceTypes={this.getMediaSourceTypes()}
          profileOwnerUser={profileOwnerUser}
          onClose={close}
        />
      </Suspense>
    );
  };

  renderDeviationPicker = (close?) => {
    const { moduleId, profileOwnerUser } = this.props;
    return (
      <Suspense fallback={<ChunkLoadingPopover />}>
        <DeviationPicker
          moduleId={moduleId}
          mediaSourceTypes={this.getMediaSourceTypes()}
          gruser={profileOwnerUser}
          onClose={close}
        />
      </Suspense>
    );
  };

  showDeviationPicker = () => {
    this.setState({ showDeviationPicker: true });
  };
  hideDeviationPicker = () => {
    this.setState({ showDeviationPicker: false });
  };
}

const enhance = compose(
  reduxConnectContainer,
  withFeatureFlags,
  withTranslation()
);

export default enhance(FeaturedDeviationBox) as React.ComponentType<{}>;
